import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { getLoginUrl } from './services/auth';
import DashboardLayout from './components/layout/DashboardLayout';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Audits from './pages/Audits';
import AuditDetail from './pages/AuditDetail';
import BrandPerception from './pages/BrandPerception';
import BrandAxisPage from './pages/BrandAxisPage';
import BrandStrategy from './pages/BrandStrategy';
import GeoStrategy from './pages/GeoStrategy';
import Competitors from './pages/Competitors';
import EarnedMedia from './pages/EarnedMedia';
import NewsWatch from './pages/NewsWatch';
import Tasks from './pages/Tasks';
import AuditReport from './pages/reports/AuditReport';
import StatusReport from './pages/reports/StatusReport';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantForm from './pages/admin/AdminTenantForm';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading, user, needsOnboarding } = useAuth();
  const unauthenticated = !loading && !user && !needsOnboarding;

  useEffect(() => {
    if (unauthenticated) {
      window.location.replace(getLoginUrl());
    }
  }, [unauthenticated]);

  if (loading || unauthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (needsOnboarding) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) return null;
  if (!user?.isPlatformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="strategy" element={<BrandStrategy />} />
        <Route path="geo-strategy" element={<GeoStrategy />} />
        <Route path="audits" element={<Audits />} />
        <Route path="audits/:auditId" element={<AuditDetail />} />
        <Route path="earned-media" element={<EarnedMedia />} />
        <Route path="brand" element={<BrandPerception />} />
        <Route path="brand/narrative" element={<BrandAxisPage axis="narrative" />} />
        <Route path="brand/authority" element={<BrandAxisPage axis="authority" />} />
        <Route path="brand/referral" element={<BrandAxisPage axis="referral" />} />
        <Route path="social" element={<div>Social Audit — Phase 5</div>} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="news-watch" element={<NewsWatch />} />
        <Route path="competitors" element={<Competitors />} />
        <Route path="reports/audit" element={<AuditReport />} />
        <Route path="reports/status" element={<StatusReport />} />
        <Route path="settings" element={<Settings />} />
        <Route
          path="admin/tenants"
          element={<PlatformAdminRoute><AdminTenants /></PlatformAdminRoute>}
        />
        <Route
          path="admin/tenants/new"
          element={<PlatformAdminRoute><AdminTenantForm /></PlatformAdminRoute>}
        />
        <Route
          path="admin/tenants/:tenantId"
          element={<PlatformAdminRoute><AdminTenantForm /></PlatformAdminRoute>}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
